/**
 * v98.9.0 · WAR ROOM · CENTRO DE OPERACIONES
 * - GPS de técnicos/contratistas actualizado cada 5 minutos (con estela de movimiento).
 * - Viajando → van vista desde arriba (estilo Waze, gira con el rumbo) con logo de Alfanet en el techo · En sitio/ejecutando → técnico de pie · Disponible → chincheta
 *   (v101.108.0, sin pin de mapa). Cajas como postes pequeños desde zoom 15.
 * - Color del técnico = color de su sucursal (relleno) + color de su zona (borde). Configurables.
 * - Colores reservados para bloqueos: polígono/zona ROJO · sucursal AMARILLO · técnico VERDE FOSFORESCENTE.
 * - Recorrido + ETA cuando el técnico viaja a la OT.
 * - Bloqueos y calamidades visibles + asistente para crear bloqueos (polígono, zona, sucursal, técnico).
 * - Ficha del técnico (KM, OTs abiertas/en progreso/cerradas, tiempos promedio).
 * - Mapa limpio con nombres de calles (CARTO Voyager).
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, Polygon, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowLeft, ChevronLeft, ChevronRight, RefreshCcw, Clock, ClipboardList, Route as RouteIcon,
  Gauge, Radio, X, Loader2, CheckCircle2, PlayCircle, CircleDashed, Filter, ShieldAlert, Palette,
  Truck, User as UserIcon, AlertTriangle, Plus, Trash2, MapPin, Pin, Building2, Hexagon, Users, Timer,
} from "lucide-react";
import api from "../lib/api";
import MassEventPolygonEditor from "../components/MassEventPolygonEditor";
import BoxPolesLayer from "../components/BoxPolesLayer";   // v101.114.0

const REFRESH_MS = 5 * 60 * 1000;
const EC_TODAY = () => new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 10);
const shiftDay = (d, n) => { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit", timeZone: "America/Guayaquil" }) : "—");
const dmyhm = (iso) => (iso ? new Date(iso).toLocaleString("es-EC", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Guayaquil" }) : "—");
const fmtMin = (m) => (m == null ? "—" : m < 60 ? `${Math.round(m)} min` : `${Math.floor(m / 60)} h ${Math.round(m % 60)} min`);
const errMsg = (e, def) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return d ? String(d) : def;
};
const RESERVED_DEFAULT = { polygon: "#DC2626", branch: "#FACC15", tech: "#39FF14" };
const BUCKET = {
  done: { color: "#16A34A", label: "Cerrada", Icon: CheckCircle2 },
  in_progress: { color: "#F59E0B", label: "En progreso", Icon: PlayCircle },
  pending: { color: "#64748B", label: "Abierta", Icon: CircleDashed },
};
const CATEGORIES = [
  ["corte_via", "Corte de vía"], ["corte_fibra", "Corte de fibra"], ["marcha", "Marcha / protesta"],
  ["concierto", "Concierto / evento"], ["clima", "Clima / inundación"], ["otro", "Otro"],
];
// v98.9.1 · CARTO ahora exige API key ("API KEY REQUIRED"). Se usa OpenStreetMap (mismo proveedor que el resto
// de mapas de la app, sin clave, con nombres de calles) y, si sus teselas fallan, Esri World Street Map (sin clave).
const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const TILE_FALLBACK_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}";
const TILE_FALLBACK_ATTR = "Tiles &copy; Esri";

// ───────────────────────── Iconos 3D ─────────────────────────
// v98.9.2 · Iconos con volumen (degradados, brillo y sombra al piso) para que se distingan sobre el mapa.
function divIcon(html, w, h, ax, ay) {
  return L.divIcon({ html, className: "", iconSize: [w, h], iconAnchor: [ax, ay], tooltipAnchor: [0, -ay + 4] });
}
function shade(hex, pct) {
  const h = String(hex || "#64748B").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h.slice(0, 6), 16);
  if (Number.isNaN(n)) return hex;
  const f = (c) => Math.max(0, Math.min(255, Math.round(pct >= 0 ? c + (255 - c) * pct : c * (1 + pct))));
  const r = f((n >> 16) & 255), g = f((n >> 8) & 255), b = f(n & 255);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}
const gid = (k, c) => `${k}_${String(c || "x").replace(/[^0-9a-z]/gi, "")}`;
const GROUND = (cx, cy, rx, ry) => `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="url(#wrShadow)"/>`;
const SHADOW_DEF = `<radialGradient id="wrShadow"><stop offset="0" stop-color="#000" stop-opacity=".45"/><stop offset="1" stop-color="#000" stop-opacity="0"/></radialGradient>`;

// v101.108.0 · Iconos sin pin de mapa:
//   · En sitio  → técnico de pie (casco + chaleco del color de la sucursal), aro del color de la zona a sus pies.
//   · Viajando  → van vista desde arriba (v101.113.0, estilo Waze) con el logo de Alfanet en el techo; gira con el rumbo.
//   · Disponible → chincheta (cabeza del color de la sucursal, aro del color de la zona).
//   · Cajas     → postes pequeños y sutiles (solo con zoom cercano).
const ALFANET_LOGO = "/logo-alfanet.png";
const DROP = "filter:drop-shadow(0 1px 1.5px rgba(0,0,0,.45))";
function markerWrap(svg, { dim, stale }) {
  const op = dim ? 0.35 : stale ? 0.6 : 1;
  return `<div style="opacity:${op};${DROP}">${svg}</div>`;
}
function footRing(cx, cy, rx, ry, ring, { selected, blocked }) {
  const base = `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="${ring || "#FFFFFF"}" fill-opacity=".35" stroke="${ring || "#FFFFFF"}" stroke-width="2"/>`;
  const bl = blocked ? `<ellipse cx="${cx}" cy="${cy}" rx="${rx + 3}" ry="${ry + 1.6}" fill="none" stroke="${RESERVED_DEFAULT.tech}" stroke-width="3"/>` : "";
  const sl = selected ? `<ellipse cx="${cx}" cy="${cy}" rx="${rx + 5}" ry="${ry + 2.6}" fill="none" stroke="#0A3747" stroke-width="2" stroke-dasharray="4 3"/>` : "";
  return base + bl + sl;
}
// Técnico de pie (en sitio / ejecutando)
function personIcon(fill, ring, o) {
  const dark = shade(fill, -0.4), light = shade(fill, 0.25);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 54" width="34" height="46">
    <defs><radialGradient id="wrSkin" cx=".35" cy=".35" r=".75"><stop offset="0" stop-color="#FDE7D2"/><stop offset="1" stop-color="#D9A27A"/></radialGradient>
      <linearGradient id="${gid("wrVest", fill)}" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${light}"/><stop offset=".6" stop-color="${fill}"/><stop offset="1" stop-color="${dark}"/></linearGradient></defs>
    ${footRing(20, 49, 11, 3.6, ring, o)}
    <path d="M14.5 33 L14 47.5 L18.6 47.5 L19.3 35 Z" fill="#1E3A5F"/><path d="M25.5 33 L26 47.5 L21.4 47.5 L20.7 35 Z" fill="#172E4B"/>
    <rect x="12.8" y="46.2" width="6.2" height="2.6" rx="1.2" fill="#3F2A1D"/><rect x="21" y="46.2" width="6.2" height="2.6" rx="1.2" fill="#3F2A1D"/>
    <path d="M10.5 21 C9 24 8.4 29 8.8 32.5 L11.6 32.5 L12.6 23.5 Z" fill="${dark}"/>
    <path d="M29.5 21 C31 24 31.6 29 31.2 32.5 L28.4 32.5 L27.4 23.5 Z" fill="${dark}"/>
    <circle cx="10.2" cy="33.4" r="1.6" fill="url(#wrSkin)"/>
    <rect x="28.2" y="31.6" width="5.6" height="5" rx="1" fill="#475569"/><rect x="29.6" y="30.3" width="2.8" height="1.6" rx=".6" fill="#475569"/>
    <path d="M12 20.5 C13.5 18.6 16.4 18 20 18 C23.6 18 26.5 18.6 28 20.5 L28.4 34.5 L11.6 34.5 Z" fill="url(#${gid("wrVest", fill)})" stroke="${dark}" stroke-width=".8"/>
    <rect x="11.8" y="26" width="16.4" height="1.9" fill="#FDE68A" opacity=".95"/><rect x="11.8" y="30.4" width="16.4" height="1.9" fill="#FDE68A" opacity=".95"/>
    <path d="M17.6 18.2 L20 23 L22.4 18.2" fill="none" stroke="#F8FAFC" stroke-width="1.3"/>
    <circle cx="20" cy="12.2" r="5.4" fill="url(#wrSkin)"/>
    <path d="M13.9 10.8 A6.2 6.2 0 0 1 26.1 10.8 L27.4 11.9 L12.6 11.9 Z" fill="#F97316" stroke="#C2410C" stroke-width=".7"/>
    <rect x="19.2" y="4.9" width="1.6" height="4.2" rx=".6" fill="#FDBA74"/>
  </svg>`;
  return divIcon(markerWrap(svg, o), 34, 46, 17, 42);
}
// v101.113.0 · Van vista desde ARRIBA, estilo Waze: gira según el rumbo del GPS (0° = norte) y lleva el logo de
// Alfanet en el techo (el logo nunca queda de cabeza). Halo del color de la zona debajo.
function vanTopIcon(fill, ring, o) {
  const dark = shade(fill, -0.4), light = shade(fill, 0.35), roof = shade(fill, 0.18);
  const hd = o.heading != null && !Number.isNaN(Number(o.heading)) ? ((Number(o.heading) % 360) + 360) % 360 : 0;
  const logoRot = hd > 90 && hd < 270 ? 180 : 0;
  const g = gid("wrVan", fill);
  const halo = `<circle cx="32" cy="32" r="25" fill="${ring || "#FFFFFF"}" fill-opacity=".16" stroke="${ring || "#FFFFFF"}" stroke-width="1.6" stroke-opacity=".75"/>`;
  const bl = o.blocked ? `<circle cx="32" cy="32" r="30.5" fill="none" stroke="${RESERVED_DEFAULT.tech}" stroke-width="3"/>` : "";
  const sl = o.selected ? `<circle cx="32" cy="32" r="31" fill="none" stroke="#0A3747" stroke-width="2" stroke-dasharray="4 3"/>` : "";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 64 64" width="50" height="50">
    <defs>
      <linearGradient id="${g}" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${dark}"/><stop offset=".22" stop-color="${fill}"/><stop offset=".5" stop-color="${light}"/><stop offset=".78" stop-color="${fill}"/><stop offset="1" stop-color="${dark}"/></linearGradient>
      <linearGradient id="wrGlass" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#334155"/><stop offset="1" stop-color="#0F172A"/></linearGradient>
      <clipPath id="wrVanLogo"><rect x="-8" y="-8" width="16" height="16" rx="3"/></clipPath>
    </defs>
    ${halo}${bl}${sl}
    <g transform="rotate(${Math.round(hd)} 32 32)">
      <rect x="20.5" y="7" width="25" height="54" rx="9" fill="#000" opacity=".22"/>
      <ellipse cx="18.2" cy="14.5" rx="2.4" ry="1.6" fill="${dark}"/><ellipse cx="45.8" cy="14.5" rx="2.4" ry="1.6" fill="${dark}"/>
      <rect x="19" y="4" width="26" height="55" rx="9" fill="url(#${g})" stroke="${dark}" stroke-width="1.4"/>
      <path d="M21.5 10.5 Q32 6.5 42.5 10.5 L41.2 16.8 Q32 14.6 22.8 16.8 Z" fill="url(#wrGlass)"/>
      <path d="M24 11 Q28 9.4 31 9" stroke="#fff" stroke-width="1" opacity=".55" fill="none" stroke-linecap="round"/>
      <rect x="22.5" y="18.5" width="19" height="35" rx="4" fill="${roof}" stroke="${dark}" stroke-width=".6"/>
      <rect x="20.6" y="20" width="1.4" height="10" rx=".7" fill="url(#wrGlass)"/><rect x="42" y="20" width="1.4" height="10" rx=".7" fill="url(#wrGlass)"/>
      <rect x="20.6" y="32" width="1.4" height="10" rx=".7" fill="url(#wrGlass)"/><rect x="42" y="32" width="1.4" height="10" rx=".7" fill="url(#wrGlass)"/>
      <path d="M23.5 54.5 L40.5 54.5 L39.5 57 L24.5 57 Z" fill="url(#wrGlass)"/>
      <rect x="21" y="5.6" width="4.6" height="2.2" rx="1" fill="#FEF08A"/><rect x="38.4" y="5.6" width="4.6" height="2.2" rx="1" fill="#FEF08A"/>
      <rect x="20.6" y="56.6" width="3.6" height="1.8" rx=".8" fill="#EF4444"/><rect x="39.8" y="56.6" width="3.6" height="1.8" rx=".8" fill="#EF4444"/>
      <g transform="translate(32 36) rotate(${logoRot})">
        <rect x="-9.2" y="-9.2" width="18.4" height="18.4" rx="4" fill="#fff"/>
        <g clip-path="url(#wrVanLogo)"><image href="${ALFANET_LOGO}" xlink:href="${ALFANET_LOGO}" x="-9.6" y="-9.6" width="19.2" height="19.2" preserveAspectRatio="xMidYMid slice"/></g>
      </g>
    </g>
  </svg>`;
  return divIcon(markerWrap(svg, o), 50, 50, 25, 25);
}
// Chincheta (disponible)
function thumbtackIcon(fill, ring, o) {
  const dark = shade(fill, -0.4), light = shade(fill, 0.4);
  const bl = o.blocked ? `<circle cx="17" cy="11" r="13" fill="none" stroke="${RESERVED_DEFAULT.tech}" stroke-width="3"/>` : "";
  const sl = o.selected ? `<circle cx="17" cy="11" r="15.5" fill="none" stroke="#0A3747" stroke-width="2" stroke-dasharray="4 3"/>` : "";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -4 34 48" width="28" height="40">
    <defs><radialGradient id="${gid("wrTack", fill)}" cx=".35" cy=".3" r=".8"><stop offset="0" stop-color="${light}"/><stop offset=".6" stop-color="${fill}"/><stop offset="1" stop-color="${dark}"/></radialGradient>
      <linearGradient id="wrNeedle" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#E5E7EB"/><stop offset=".5" stop-color="#9CA3AF"/><stop offset="1" stop-color="#6B7280"/></linearGradient></defs>
    <ellipse cx="17" cy="42" rx="4.5" ry="1.4" fill="#000" opacity=".3"/>
    <path d="M16 26 L18 26 L17.3 42 L16.7 42 Z" fill="url(#wrNeedle)"/>
    <path d="M10 22.5 L24 22.5 L22 27 L12 27 Z" fill="${dark}"/>
    <path d="M12.5 13 L21.5 13 L23 22.8 L11 22.8 Z" fill="url(#${gid("wrTack", fill)})"/>
    <ellipse cx="17" cy="11" rx="10" ry="8.6" fill="url(#${gid("wrTack", fill)})" stroke="${ring || "#fff"}" stroke-width="2.4"/>
    <ellipse cx="13.6" cy="7.6" rx="3.6" ry="2.2" fill="#fff" opacity=".45" transform="rotate(-25 13.6 7.6)"/>
    ${bl}${sl}
  </svg>`;
  return divIcon(markerWrap(svg, o), 28, 40, 14, 38);
}
function techIcon(t, selected, dim) {
  const opts = {
    selected, dim, blocked: !!(t.block || t.calamity),
    stale: (t.last_ping?.gps_status || "offline") !== "live", heading: t.state === "traveling" ? t.last_ping?.heading : null,
  };
  const fill = t.branch_color || "#64748B";
  const ring = t.zone_color || "#FFFFFF";
  if (t.state === "traveling") return vanTopIcon(fill, ring, opts);
  if (t.state === "on_site") return personIcon(fill, ring, opts);
  return thumbtackIcon(fill, ring, opts);
}
// Casa 3D (isométrica) — techo con el color del estado de la OT
function otIcon(o, { big = false, seq = null, current = false } = {}) {
  const roof = BUCKET[o.bucket]?.color || "#64748B";
  const sz = big ? 42 : 32;
  const badge = seq != null
    ? `<circle cx="34" cy="7" r="7" fill="#0A3747" stroke="#fff" stroke-width="1.5"/><text x="34" y="10.5" text-anchor="middle" font-size="9" font-weight="800" fill="#fff" font-family="system-ui">${seq}</text>` : "";
  const ring = current ? `<ellipse cx="20" cy="37" rx="17" ry="5" fill="none" stroke="${roof}" stroke-width="2.5"/>` : "";
  const late = o.is_delayed ? `<circle cx="7" cy="9" r="5" fill="#DC2626" stroke="#fff" stroke-width="1.2"/><text x="7" y="12" text-anchor="middle" font-size="8" font-weight="900" fill="#fff" font-family="system-ui">!</text>` : "";
  return divIcon(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42 42" width="${sz}" height="${sz}">
    <defs>${SHADOW_DEF}</defs>
    ${GROUND(20, 37, 15, 3.5)}${ring}
    <path d="M6 20 L20 27 L20 38 L6 31 Z" fill="#F1F5F9" stroke="#94A3B8" stroke-width=".7"/>
    <path d="M20 27 L34 20 L34 31 L20 38 Z" fill="#CBD5E1" stroke="#94A3B8" stroke-width=".7"/>
    <path d="M4 20.5 L13 7 L22 27.5 L20 28.5 Z" fill="${shade(roof, 0.15)}" stroke="${shade(roof, -0.4)}" stroke-width=".8"/>
    <path d="M13 7 L27 1 L36 20.5 L22 27.5 Z" fill="${shade(roof, -0.2)}" stroke="${shade(roof, -0.45)}" stroke-width=".8"/>
    <path d="M24 30 L28.5 27.8 L28.5 34.5 L24 36.7 Z" fill="#7C5A3A"/>
    <path d="M9 25 L13.5 27.3 L13.5 31 L9 28.7 Z" fill="#BFE3F7" stroke="#fff" stroke-width=".5"/>
    ${late}${badge}
  </svg>`, sz, sz, sz / 2, sz * 0.9);
}
// Calamidad 3D: señal de alerta con aro verde (técnico afectado)
function calamityIcon() {
  return divIcon(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 44" width="36" height="40">
    <defs>${SHADOW_DEF}<linearGradient id="wrCal" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#FDBA74"/><stop offset="1" stop-color="#C2410C"/></linearGradient></defs>
    ${GROUND(20, 41, 12, 3)}
    <circle cx="20" cy="20" r="18" fill="#0F172A" stroke="${RESERVED_DEFAULT.tech}" stroke-width="3.5"/>
    <path d="M20 7 L32 29 L8 29 Z" fill="url(#wrCal)" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/>
    <rect x="18.6" y="13" width="2.8" height="9" rx="1.2" fill="#fff"/><circle cx="20" cy="25.3" r="1.6" fill="#fff"/>
  </svg>`, 36, 40, 18, 38);
}
// Técnico bloqueado (sin estar en el mapa como activo)
function blockedTechIcon() {
  return divIcon(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 44" width="34" height="38">
    <defs>${SHADOW_DEF}</defs>${GROUND(20, 41, 11, 3)}
    <circle cx="20" cy="20" r="17" fill="#1E293B" stroke="${RESERVED_DEFAULT.tech}" stroke-width="3.5"/>
    <circle cx="20" cy="15" r="4.5" fill="#E2E8F0"/><path d="M12 29 C12 22 15 20 20 20 C25 20 28 22 28 29 Z" fill="#E2E8F0"/>
    <path d="M10 10 L30 30" stroke="${RESERVED_DEFAULT.tech}" stroke-width="3" stroke-linecap="round"/>
  </svg>`, 34, 38, 17, 36);
}
// Barricada 3D en el centro de un bloqueo de área
function barricadeIcon(color) {
  const c2 = shade(color, -0.35);
  return divIcon(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 36" width="40" height="33">
    <defs>${SHADOW_DEF}<pattern id="${gid("wrStripe", color)}" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="4" height="8" fill="${color}"/><rect x="4" width="4" height="8" fill="#fff"/></pattern></defs>
    ${GROUND(22, 33, 18, 3)}
    <rect x="7" y="18" width="3" height="14" fill="#475569"/><rect x="34" y="18" width="3" height="14" fill="#475569"/>
    <path d="M3 8 L41 8 L41 18 L3 18 Z" fill="url(#${gid("wrStripe", color)})" stroke="${c2}" stroke-width="1.2"/>
    <path d="M3 18 L41 18 L39 21 L5 21 Z" fill="${c2}"/>
    <circle cx="8" cy="5" r="3" fill="#F97316" stroke="#fff" stroke-width="1"/><circle cx="36" cy="5" r="3" fill="#F97316" stroke="#fff" stroke-width="1"/>
  </svg>`, 40, 33, 20, 31);
}
function etaLabelIcon(text) {
  return L.divIcon({
    className: "",
    // v101.26.0 · El ícono mide 0×0: sin width:max-content el fondo quedaba en un círculo y el texto
    // blanco se salía sobre el mapa (ilegible). Ahora la píldora envuelve todo el texto.
    html: `<div style="display:inline-block;width:max-content;transform:translate(-50%,-130%);white-space:nowrap;background:#0A3747;color:#fff;font:700 11px/1.3 system-ui;padding:3px 9px;border-radius:999px;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.45);pointer-events:none">${text}</div>`,
    iconSize: [0, 0],
  });
}
const havKm = (a, b) => {
  const R = 6371, toR = (x) => (x * Math.PI) / 180;
  const dLa = toR(b[0] - a[0]), dLo = toR(b[1] - a[1]);
  const h = Math.sin(dLa / 2) ** 2 + Math.cos(toR(a[0])) * Math.cos(toR(b[0])) * Math.sin(dLo / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

function FitTo({ points, trigger }) {
  const map = useMap();
  useEffect(() => {
    const pts = (points || []).filter((p) => p && p[0] != null && p[1] != null);
    if (pts.length === 1) map.setView(pts[0], 15);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [50, 50], maxZoom: 16 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return null;
}

function Kpi({ icon: Icon, label, value, sub, color = "#0A3747" }) {
  return (
    <div className="flex-1 min-w-[140px] bg-white rounded-xl border border-gray-200 px-3 py-2 shadow-sm">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
        <Icon size={12} style={{ color }} /> {label}
      </div>
      <div className="text-xl font-black mt-0.5" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500">{sub}</div>}
    </div>
  );
}

function Chip({ checked, onChange, label, count, testid }) {
  return (
    <button onClick={() => onChange(!checked)} data-testid={testid || `chip-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded-full border font-semibold transition-colors ${
        checked ? "bg-[#0A3747] text-white border-[#0A3747]" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
      }`}>
      {label}{count != null && <span className={`text-[10px] ${checked ? "text-[#E0E621]" : "text-gray-400"}`}>({count})</span>}
    </button>
  );
}

function Modal({ title, onClose, children, width = 520, testid }) {
  return (
    <div className="fixed inset-0 z-[2000] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] flex flex-col" style={{ maxWidth: width }}
        onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <div className="font-black text-[#0A3747] text-sm flex-1">{title}</div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}

// ───────────────────────── Página ─────────────────────────
export default function WarRoomCC() {
  const navigate = useNavigate();
  const [date, setDate] = useState(EC_TODAY());
  const EMPTY_F = { branch_id: "", zone_id: "", tech_id: "", client: "", solution_node_id: "", ot: "" };
  const [f, setF] = useState(EMPTY_F);
  const [technicians, setTechnicians] = useState([]);
  const [data, setData] = useState({ kpis: {}, techs: [], ots: [], solution_options: [], branches: [], zones: [] });
  const [catalog, setCatalog] = useState({ branches: [], zones: [] });
  const [blocks, setBlocks] = useState({ areas: [], techs: [], calamities: [] });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [nextIn, setNextIn] = useState(REFRESH_MS / 1000);
  const [sel, setSel] = useState(null);
  const [track, setTrack] = useState([]);
  const [showTechs, setShowTechs] = useState(true);
  const [showOts, setShowOts] = useState(false);
  const [showBlocks, setShowBlocks] = useState(true);
  const [showTrails, setShowTrails] = useState(true);
  const [showBoxes, setShowBoxes] = useState(true);
  const [boxCount, setBoxCount] = useState(null);
  const [focusOt, setFocusOt] = useState(null);   // v101.114.0 · OT clicada → cajas alrededor
  const [onlyTraveling, setOnlyTraveling] = useState(false);
  const [onlyOnSite, setOnlyOnSite] = useState(false);
  const [onlyDelayed, setOnlyDelayed] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [panel, setPanel] = useState(null); // "blocks" | null
  const [wizardOpen, setWizardOpen] = useState(false);
  const [colorsOpen, setColorsOpen] = useState(false);
  const [fitKey, setFitKey] = useState(0);
  const lastLoadRef = useRef(Date.now());
  const [tileFallback, setTileFallback] = useState(false);
  const tileErrRef = useRef(0);

  useEffect(() => {
    api.get("/v1/ot_smart/filter-options")
      .then(({ data }) => setTechnicians(data.technicians || []))
      .catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = { date };
    Object.entries(f).forEach(([k, v]) => { if (v && String(v).trim()) p[k] = String(v).trim(); });
    return p;
  }, [date, f]);

  const loadBlocks = useCallback(async () => {
    try { const { data } = await api.get("/v1/warroom/cc/blocks"); setBlocks(data || { areas: [], techs: [], calamities: [] }); }
    catch { /* silencioso */ }
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [cc] = await Promise.allSettled([api.get("/v1/warroom/cc", { params }), loadBlocks()]);
      if (cc.status === "fulfilled") {
        const d = cc.value.data || {};
        setData(d);
        if ((d.branches || []).length || (d.zones || []).length) setCatalog({ branches: d.branches || [], zones: d.zones || [] });
        setLastUpdate(new Date());
      } else if (!silent) toast.error(errMsg(cc.reason, "No se pudo cargar el centro de operaciones"));
      if (!silent) setFitKey((k) => k + 1);
    } finally {
      lastLoadRef.current = Date.now();
      if (!silent) setLoading(false);
    }
  }, [params, loadBlocks]);

  useEffect(() => { load(false); }, [load]);
  // Refresco automático cada 5 minutos + contador visible
  useEffect(() => {
    const t = setInterval(() => {
      const left = Math.max(0, Math.round((REFRESH_MS - (Date.now() - lastLoadRef.current)) / 1000));
      setNextIn(left);
      if (left <= 0) load(true);
    }, 1000);
    return () => clearInterval(t);
  }, [load]);

  // Recorrido GPS del día del técnico seleccionado; al seleccionarlo se encuadra su ruta.
  const fittedForRef = useRef(null);
  useEffect(() => {
    if (!sel) { setTrack([]); fittedForRef.current = null; return; }
    api.get(`/v1/warroom/cc/track/${sel}`, { params: { date } })
      .then(({ data }) => { setTrack((data.points || []).map((p) => [p.lat, p.lon])); })
      .catch(() => setTrack([]))
      .finally(() => {
        if (fittedForRef.current !== `${sel}|${date}`) { fittedForRef.current = `${sel}|${date}`; setFitKey((k) => k + 1); }
      });
  }, [sel, date, lastUpdate]);
  // Al filtrar por técnico se selecciona automáticamente (muestra su ruta y su ficha).
  useEffect(() => {
    if (f.tech_id && (data.techs || []).some((t) => t.tech_id === f.tech_id)) setSel(f.tech_id);
  }, [f.tech_id, data.techs]);

  const techs = data.techs || [];
  let mapTechs = techs.filter((t) => t.last_ping?.lat != null);
  if (onlyTraveling) mapTechs = mapTechs.filter((t) => t.state === "traveling");
  if (onlyOnSite) mapTechs = mapTechs.filter((t) => t.state === "on_site");
  const selTech = techs.find((t) => t.tech_id === sel);

  let visibleOts = (data.ots || []).filter((o) => o.bucket !== "cancelled" && o.lat != null);
  if (sel) visibleOts = visibleOts.filter((o) => o.uid === sel);
  if (onlyDelayed) visibleOts = visibleOts.filter((o) => o.is_delayed);
  if (onlyOnSite) visibleOts = visibleOts.filter((o) => o.is_on_site);
  if (onlyTraveling) visibleOts = visibleOts.filter((o) => o.is_traveling);
  const otsOnMap = showOts || onlyDelayed || sel ? visibleOts : [];

  const fitPoints = sel
    ? [...track, ...(selTech?.last_ping ? [[selTech.last_ping.lat, selTech.last_ping.lon]] : []), ...visibleOts.map((o) => [o.lat, o.lon])]
    : mapTechs.length ? mapTechs.map((t) => [t.last_ping.lat, t.last_ping.lon]) : visibleOts.map((o) => [o.lat, o.lon]);
  // Ruta del técnico seleccionado: OTs del día en orden de agenda.
  const selRoute = useMemo(() => {
    if (!sel) return null;
    const list = (data.ots || []).filter((o) => o.uid === sel && o.bucket !== "cancelled" && o.lat != null)
      .slice().sort((a, b) => String(a.scheduled_at || "").localeCompare(String(b.scheduled_at || "")));
    const seq = Object.fromEntries(list.map((o, i) => [o.odoo_task_id, i + 1]));
    const done = list.filter((o) => o.bucket === "done");
    const active = list.find((o) => o.is_on_site) || list.find((o) => o.is_traveling) || list.find((o) => o.bucket === "in_progress");
    const upcoming = list.filter((o) => o.bucket !== "done" && o !== active);
    const here = selTech?.last_ping ? [selTech.last_ping.lat, selTech.last_ping.lon] : null;
    const histLine = done.map((o) => [o.lat, o.lon]);
    const nextStops = [...(active ? [active] : []), ...upcoming];
    const planLine = [...(here ? [here] : histLine.length ? [histLine[histLine.length - 1]] : []), ...nextStops.map((o) => [o.lat, o.lon])];
    const firstLeg = here && nextStops[0] ? { from: here, to: [nextStops[0].lat, nextStops[0].lon], km: havKm(here, [nextStops[0].lat, nextStops[0].lon]), ot: nextStops[0] } : null;
    const planKm = planLine.reduce((acc, p, i) => (i ? acc + havKm(planLine[i - 1], p) : 0), 0);
    return { list, seq, histLine, planLine, firstLeg, active, planKm };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, data.ots, selTech?.last_ping?.lat, selTech?.last_ping?.lon]);

  // v101.114.0 · Puntos alrededor de los cuales se muestran las cajas (a cualquier zoom)
  const boxFocus = [
    ...(focusOt && focusOt.lat != null ? [{ lat: focusOt.lat, lon: focusOt.lon }] : []),
    ...(selTech?.last_ping?.lat != null ? [{ lat: selTech.last_ping.lat, lon: selTech.last_ping.lon }] : []),
    ...((selRoute?.list || []).filter((o) => o.bucket !== "done").map((o) => ({ lat: o.lat, lon: o.lon }))),
  ].slice(0, 8);

  // Calamidades y técnicos bloqueados ubicados en el mapa
  const onMapIds = new Set(mapTechs.map((t) => t.tech_id));
  const calamityMarkers = (blocks.calamities || []).filter((c) => c.location?.lat != null);
  const blockedTechMarkers = (blocks.techs || []).flatMap((b) => (b.locations || [])
    .filter((l) => !onMapIds.has(l.tech_id)).map((l) => ({ ...l, block: b })));
  const k = data.kpis || {};
  const noGps = techs.length - techs.filter((t) => t.last_ping?.lat != null).length;
  const reserved = data.reserved_colors || RESERVED_DEFAULT;

  const zonesForBranch = (catalog.zones || []).filter((z) => !f.branch_id || z.branch_id === f.branch_id);
  const set = (key) => (e) => {
    const v = e.target.value;
    setF((x) => ({ ...x, [key]: v, ...(key === "branch_id" ? { zone_id: "" } : {}) }));
    setSel(null);
  };
  const hasFilters = Object.values(f).some((v) => v);
  const blockCount = (blocks.areas?.length || 0) + (blocks.techs?.length || 0) + (blocks.calamities?.length || 0);

  const legendBranches = useMemo(() => {
    const ids = new Set(mapTechs.map((t) => t.branch_id).filter(Boolean));
    return (catalog.branches || []).filter((b) => ids.has(b.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapTechs.length, catalog.branches, data.techs]);

  const resolveArea = async (a) => {
    if (!window.confirm(`¿Levantar el bloqueo "${a.title}"? Las OTs afectadas se liberarán.`)) return;
    try { await api.post(`/v1/mass-events/${a.id}/resolve`, { release_ots: true, reason: "Levantado desde War Room" }); toast.success("Bloqueo levantado"); load(true); }
    catch (e) { toast.error(errMsg(e, "No se pudo levantar el bloqueo")); }
  };
  const removeTechBlock = async (b) => {
    if (!window.confirm(`¿Quitar el bloqueo de ${b.tech_names.join(", ")}?`)) return;
    try { await api.delete(`/v1/mass-events/tech-events/${b.id}`); toast.success("Bloqueo de técnico eliminado"); load(true); }
    catch (e) { toast.error(errMsg(e, "No se pudo eliminar el bloqueo")); }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F4] flex flex-col" data-testid="warroom-cc">
      {/* Encabezado */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-[#0A3747] text-white">
        <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/apps"))}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold" data-testid="warroom-back">
          <ArrowLeft size={14} /> Volver
        </button>
        <div className="font-black tracking-wide text-[#E0E621]">WAR ROOM · CENTRO DE OPERACIONES</div>
        <div className="text-[10px] opacity-80" data-testid="warroom-last-update">
          {lastUpdate ? `Actualizado ${lastUpdate.toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit" })}` : ""}
          {" · "}próxima en {Math.floor(nextIn / 60)}:{String(nextIn % 60).padStart(2, "0")}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => { setDate(shiftDay(date, -1)); setSel(null); }} className="p-1.5 rounded bg-white/10 hover:bg-white/20"><ChevronLeft size={14} /></button>
          <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setSel(null); }} className="px-2 py-1 rounded text-xs text-[#0A3747]" />
          <button onClick={() => { setDate(shiftDay(date, 1)); setSel(null); }} className="p-1.5 rounded bg-white/10 hover:bg-white/20"><ChevronRight size={14} /></button>
          <button onClick={() => { setDate(EC_TODAY()); setSel(null); }} className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-[11px] font-bold">Hoy</button>
          <button onClick={() => load(false)} className="p-1.5 rounded bg-[#E0E621] text-[#0A3747]" title="Actualizar ahora" data-testid="warroom-refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="flex flex-wrap gap-2 px-4 pt-3" data-testid="warroom-kpis">
        <Kpi icon={Radio} label="Técnicos en línea" value={`${k.techs_live ?? 0}/${k.techs_total ?? 0}`} sub={`GPS < 15 min · ${noGps} sin señal`} color="#2563EB" />
        <Kpi icon={Truck} label="Viajando" value={k.techs_traveling ?? 0} sub="en ruta a una OT" color="#0891B2" />
        <Kpi icon={UserIcon} label="En sitio" value={k.techs_on_site ?? 0} sub="ejecutando una OT" color="#EA580C" />
        <Kpi icon={ShieldAlert} label="Bloqueos / calamidades" value={blockCount} sub={`${k.techs_blocked ?? 0} técnicos afectados`} color="#DC2626" />
        <Kpi icon={ClipboardList} label="Órdenes del día" value={k.ots_day ?? 0}
          sub={`${k.ots_done ?? 0} cerradas · ${k.ots_in_progress ?? 0} en curso · ${k.ots_pending ?? 0} abiertas`} color="#0A3747" />
        <Kpi icon={RouteIcon} label="Km recorridos" value={`${k.km_total ?? 0} km`} sub="según GPS del día" color="#7C3AED" />
        <Kpi icon={Gauge} label="Ocupación" value={k.occupancy_pct != null ? `${k.occupancy_pct}%` : "—"} sub="agendado / turnos" color="#16A34A" />
      </div>

      {/* Chips + acciones */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-2" data-testid="warroom-filters">
        <Chip checked={showTechs} onChange={setShowTechs} label="Técnicos" count={mapTechs.length} />
        <Chip checked={showTrails} onChange={setShowTrails} label="Recorridos" testid="chip-recorridos" />
        <Chip checked={showBlocks} onChange={setShowBlocks} label="Bloqueos" count={blockCount} />
        <Chip checked={showOts} onChange={setShowOts} label="OTs" count={visibleOts.length} />
        <Chip checked={showBoxes} onChange={setShowBoxes} label="Cajas" count={showBoxes ? boxCount ?? undefined : undefined} testid="chip-cajas" />
        <span className="w-px h-5 bg-gray-300" />
        <Chip checked={onlyTraveling} onChange={(v) => { setOnlyTraveling(v); if (v) setOnlyOnSite(false); }} label="Viajando" />
        <Chip checked={onlyOnSite} onChange={(v) => { setOnlyOnSite(v); if (v) setOnlyTraveling(false); }} label="En sitio" />
        <Chip checked={onlyDelayed} onChange={setOnlyDelayed} label="Retrasadas" count={(data.ots || []).filter((o) => o.is_delayed).length} />

        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setPanel(panel === "blocks" ? null : "blocks")} data-testid="warroom-blocks-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border ${panel === "blocks" ? "bg-red-600 text-white border-red-600" : "bg-white border-gray-200 hover:bg-gray-50"}`}>
            <ShieldAlert size={13} /> Bloqueos y calamidades
          </button>
          <button onClick={() => setWizardOpen(true)} data-testid="warroom-new-block"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[#0A3747] text-white font-bold">
            <Plus size={13} /> Nuevo bloqueo
          </button>
          <button onClick={() => setColorsOpen(true)} data-testid="warroom-colors-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border bg-white border-gray-200 hover:bg-gray-50">
            <Palette size={13} /> Colores
          </button>
          <div className="relative">
            <button onClick={() => setFilterOpen(!filterOpen)} data-testid="warroom-filter-btn"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border ${hasFilters ? "bg-[#0A3747] text-white border-[#0A3747]" : "bg-white border-gray-200 hover:bg-gray-50"}`}>
              <Filter size={13} /> Filtros {hasFilters && <span className="bg-[#E0E621] text-[#0A3747] rounded-full w-4 h-4 text-[10px] font-bold flex items-center justify-center">{Object.values(f).filter(Boolean).length}</span>}
            </button>
            {filterOpen && (
              <div className="absolute right-0 top-full mt-1 z-[1200] bg-white rounded-xl border border-gray-200 shadow-xl p-3 w-[320px] space-y-2" data-testid="warroom-filter-panel">
                <div className="text-[11px] font-bold text-[#0A3747] mb-1">Filtros</div>
                <select value={f.branch_id} onChange={set("branch_id")} className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200" data-testid="filter-branch">
                  <option value="">Todas las sucursales</option>
                  {(catalog.branches || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <select value={f.zone_id} onChange={set("zone_id")} className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200" data-testid="filter-zone">
                  <option value="">Todas las zonas</option>
                  {zonesForBranch.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
                <select value={f.tech_id} onChange={set("tech_id")} className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200" data-testid="filter-tech">
                  <option value="">Todos los técnicos / contratistas</option>
                  {technicians.map((t) => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
                </select>
                <select value={f.solution_node_id} onChange={set("solution_node_id")} className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200">
                  <option value="">Todos los tipos de solución</option>
                  {(data.solution_options || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <input value={f.client} onChange={set("client")} placeholder="Cliente" className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200" />
                <input value={f.ot} onChange={set("ot")} placeholder="N.º de OT" className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200" />
                <div className="flex gap-2 pt-1">
                  <button onClick={() => { setF(EMPTY_F); setSel(null); }} className="flex-1 px-2 py-1.5 text-xs rounded-lg border border-gray-300 bg-white hover:bg-gray-50">Limpiar</button>
                  <button onClick={() => setFilterOpen(false)} className="flex-1 px-2 py-1.5 text-xs rounded-lg bg-[#0A3747] text-white font-bold">Cerrar</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mapa */}
      <div className="relative flex-1 mx-4 mb-4 rounded-xl overflow-hidden border border-gray-200 min-h-[560px]">
        <style>{`.warroom-tiles{filter:saturate(0.55) brightness(1.03);}`}</style>
        <MapContainer center={[-1.4, -78.5]} zoom={7} minZoom={6} maxBounds={[[2.5, -82.5], [-5.5, -74.5]]} maxBoundsViscosity={1.0} style={{ height: "100%", width: "100%", minHeight: 560 }}>
          <TileLayer key={tileFallback ? "esri" : "osm"} url={tileFallback ? TILE_FALLBACK_URL : TILE_URL}
            attribution={tileFallback ? TILE_FALLBACK_ATTR : TILE_ATTR} maxZoom={19} className="warroom-tiles"
            eventHandlers={{ tileerror: () => { tileErrRef.current += 1; if (!tileFallback && tileErrRef.current >= 6) setTileFallback(true); } }} />
          <FitTo points={fitPoints} trigger={fitKey} />
          {/* v101.114.0 · Cajas como postes: al acercar (zoom 15+) y alrededor del técnico / OT seleccionados */}
          <BoxPolesLayer enabled={showBoxes} onCount={setBoxCount} focus={boxFocus} radiusM={600} />

          {/* Bloqueos de área: polígono/zona (rojo) y sucursal (amarillo) */}
          {showBlocks && (blocks.areas || []).map((a) => (a.rings || []).map((ring, i) => (
            <Polygon key={`ar-${a.id}-${i}`} positions={ring}
              pathOptions={{ color: a.color, fillColor: a.color, weight: 2.5, fillOpacity: a.area_type === "branch" ? 0.16 : 0.22, dashArray: "8 5" }}>
              <Tooltip sticky>
                <div className="text-[11px]"><b>Bloqueo · {a.label}</b><br />{a.title}<br />
                  {a.affected_ot_count} OTs afectadas · hasta {dmyhm(a.ends_at)}</div>
              </Tooltip>
            </Polygon>
          )))}
          {showBlocks && (blocks.areas || []).filter((a) => a.center?.lat != null).map((a) => (
            <Marker key={`arc-${a.id}`} position={[a.center.lat, a.center.lon]} icon={barricadeIcon(a.color)} zIndexOffset={200}>
              <Tooltip direction="top">
                <div className="text-[11px]"><b>Bloqueo · {a.label}</b><br />{a.title}<br />
                  {a.affected_ot_count} OTs afectadas · hasta {dmyhm(a.ends_at)}
                  {!a.rings?.length && <><br /><i>Ubicado por sus OTs afectadas (la zona no tiene polígono)</i></>}</div>
              </Tooltip>
            </Marker>
          ))}

          {/* Calamidades (en la última posición conocida del técnico) */}
          {showBlocks && calamityMarkers.map((c) => (
            <Marker key={`cal-${c.id}`} position={[c.location.lat, c.location.lon]} icon={calamityIcon()} zIndexOffset={900}
              eventHandlers={{ click: () => c.tech_id && setSel(c.tech_id) }}>
              <Tooltip direction="top">
                <div className="text-[11px] max-w-[220px]"><b>Calamidad · {c.category}</b><br />{c.tech_name || "Técnico"}<br />
                  {c.description || ""}{c.description ? <br /> : null}desde {dmyhm(c.since)}<br />
                  <span className="text-gray-500">{c.location.source === "gps" ? `Última señal GPS ${dmyhm(c.location.at)}` : "Ubicado en su OT del día (sin GPS)"}</span></div>
              </Tooltip>
            </Marker>
          ))}
          {/* Técnicos bloqueados que no están activos en el mapa */}
          {showBlocks && blockedTechMarkers.map((m) => (
            <Marker key={`tb-${m.block.id}-${m.tech_id}`} position={[m.lat, m.lon]} icon={blockedTechIcon()} zIndexOffset={800}>
              <Tooltip direction="top">
                <div className="text-[11px]"><b>Técnico bloqueado</b><br />{m.tech_name}<br />{m.block.reason}<br />
                  {dmyhm(m.block.from)} → {dmyhm(m.block.until)}</div>
              </Tooltip>
            </Marker>
          ))}

          {/* Estela de movimiento (última hora) */}
          {showTechs && showTrails && mapTechs.filter((t) => (t.trail || []).length > 1 && t.state !== "traveling").map((t) => (
            <Polyline key={`tr-${t.tech_id}`} positions={t.trail} pathOptions={{ color: t.branch_color || "#64748B", weight: 3, opacity: 0.45 }} />
          ))}

          {/* Recorrido hacia la OT + ETA cuando viaja */}
          {showTechs && mapTechs.filter((t) => t.state === "traveling").map((t) => {
            const here = [t.last_ping.lat, t.last_ping.lon];
            const dest = t.active_ot && t.active_ot.lat != null ? [t.active_ot.lat, t.active_ot.lon] : null;
            const travelled = (t.travel_trail || []).length > 1 ? t.travel_trail : (showTrails ? t.trail : []);
            return (
              <Fragment key={`trv-${t.tech_id}`}>
                {travelled && travelled.length > 1 && (
                  <Polyline positions={travelled} pathOptions={{ color: t.branch_color || "#0891B2", weight: 5, opacity: 0.85 }} />
                )}
                {dest && (
                  <>
                    <Polyline positions={[here, dest]} pathOptions={{ color: t.branch_color || "#0891B2", weight: 3, dashArray: "2 8", opacity: 0.9, lineCap: "round" }} />
                    {t.tech_id !== sel && <Marker position={dest} icon={otIcon(t.active_ot, { big: true, current: true })}
                      eventHandlers={{ click: () => setFocusOt({ lat: dest[0], lon: dest[1] }) }}>
                      <Tooltip direction="top"><div className="text-[11px]"><b>{t.active_ot.number}</b> · destino de {t.name}<br />{t.active_ot.client_name}<br />{t.active_ot.address || ""}</div></Tooltip>
                    </Marker>}
                    {t.eta && (
                      <Marker position={[(here[0] + dest[0]) / 2, (here[1] + dest[1]) / 2]} interactive={false}
                        icon={etaLabelIcon(`Llega en ${fmtMin(t.eta.minutes)} · ${t.eta.km} km · ${hhmm(t.eta.arrive_at)}`)} />
                    )}
                  </>
                )}
              </Fragment>
            );
          })}

          {/* Ruta del día del técnico seleccionado */}
          {sel && track.length > 1 && <Polyline positions={track} pathOptions={{ color: "#2563EB", weight: 5, opacity: 0.8 }} />}
          {/* Trazo a las OTs: atendidas (gris) y pendientes en orden desde la posición actual (punteado) */}
          {selRoute && selRoute.histLine.length > 1 && (
            <Polyline positions={selRoute.histLine} pathOptions={{ color: "#64748B", weight: 3, opacity: 0.7 }} />
          )}
          {selRoute && selRoute.planLine.length > 1 && (
            <Polyline positions={selRoute.planLine}
              pathOptions={{ color: selTech?.branch_color || "#0A3747", weight: 4, opacity: 0.95, dashArray: "10 8", lineCap: "round" }} />
          )}
          {selRoute?.firstLeg && selTech?.state !== "traveling" && (
            <Marker interactive={false}
              position={[(selRoute.firstLeg.from[0] + selRoute.firstLeg.to[0]) / 2, (selRoute.firstLeg.from[1] + selRoute.firstLeg.to[1]) / 2]}
              icon={etaLabelIcon(`${selRoute.firstLeg.ot.number} · ${selRoute.firstLeg.km.toFixed(1)} km en línea recta`)} />
          )}

          {/* OTs */}
          {otsOnMap.map((o) => (
            <Marker key={`ot-${o.odoo_task_id}`} position={[o.lat, o.lon]} zIndexOffset={sel ? 300 : 0}
              icon={otIcon(o, { big: !!sel, seq: sel ? selRoute?.seq[o.odoo_task_id] : null, current: !!sel && selRoute?.active === o })}
              eventHandlers={{ click: () => setFocusOt(o) }}>
              <Tooltip direction="top">
                <div className="text-[11px]">{sel && selRoute?.seq[o.odoo_task_id] ? <b>#{selRoute.seq[o.odoo_task_id]} · </b> : null}<b>{o.number}</b> · {BUCKET[o.bucket]?.label}{o.is_delayed ? " · RETRASADA" : ""}<br />
                  {o.client_name}<br />{o.solution_name || o.activity_type || ""} · {hhmm(o.scheduled_at)}</div>
              </Tooltip>
            </Marker>
          ))}

          {/* Técnicos */}
          {showTechs && mapTechs.map((t) => {
            const isSel = t.tech_id === sel;
            return (
              <Marker key={`t-${t.tech_id}`} position={[t.last_ping.lat, t.last_ping.lon]} icon={techIcon(t, isSel, !!sel && !isSel)}
                zIndexOffset={isSel ? 1000 : t.state === "traveling" ? 600 : t.state === "on_site" ? 400 : 0}
                eventHandlers={{ click: () => { setFocusOt(null); setSel(t.tech_id); } }}>
                <Tooltip direction="top">
                  <div className="text-[11px] min-w-[180px]" data-testid={`tech-tooltip-${t.tech_id}`}>
                    <div className="font-bold text-[#0A3747]">{t.name}</div>
                    <div className="text-gray-500">{t.branch_name || "Sin sucursal"}{t.zone_name ? ` · ${t.zone_name}` : ""}</div>
                    <div className="mt-0.5">
                      {t.state === "traveling" ? "🚐 Viajando" : t.state === "on_site" ? "🧑‍🔧 En sitio" : "Disponible"}
                      {t.active_ot ? ` · ${t.active_ot.number}` : ""}
                    </div>
                    {t.state === "traveling" && t.eta && <div>Llega en <b>{fmtMin(t.eta.minutes)}</b> ({t.eta.km} km)</div>}
                    {t.block && <div className="text-green-700 font-bold">Bloqueado: {t.block.reason}</div>}
                    {t.calamity && <div className="text-red-600 font-bold">Calamidad: {t.calamity.category}</div>}
                    <div className="text-gray-500">Señal: {hhmm(t.last_ping.captured_at)} (hace {fmtMin(t.last_ping.minutes_since)})</div>
                    <div className="text-[10px] text-blue-600">Clic para ver la ficha</div>
                  </div>
                </Tooltip>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Leyenda */}
        <div className="absolute bottom-3 left-3 z-[500] bg-white/95 border border-gray-200 rounded-lg px-3 py-2 text-[10px] shadow-lg max-w-[230px] max-h-[60%] overflow-y-auto" data-testid="warroom-legend">
          <div className="font-bold text-[#0A3747] mb-1">Técnicos / contratistas</div>
          <div className="flex items-center gap-1.5"><Truck size={12} className="text-[#0891B2]" /> Van = viajando a la OT (apunta hacia donde va)</div>
          <div className="flex items-center gap-1.5"><UserIcon size={12} className="text-[#EA580C]" /> Técnico de pie = en sitio / ejecutando</div>
          <div className="flex items-center gap-1.5"><Pin size={12} className="text-gray-500" /> Chincheta = disponible</div>
          <div className="text-gray-500">Color = sucursal · aro = zona · atenuado = sin GPS reciente</div>
          <div className="font-bold text-[#0A3747] mt-1.5 mb-0.5">Cajas (postes)</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2 rounded-sm bg-[#2563EB]" /> Distribución</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2 rounded-sm bg-[#0A3747]" /> Transmisión (TD)</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full bg-[#22C55E]" /> Con puertos libres</div>
          <div className="text-gray-500">Aparecen al acercar el mapa y alrededor del técnico u OT que se seleccione</div>
          <div className="font-bold text-[#0A3747] mt-1.5 mb-0.5">Bloqueos</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm" style={{ background: reserved.polygon }} /> Polígono / zona</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm" style={{ background: reserved.branch }} /> Sucursal</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full border-[3px]" style={{ borderColor: reserved.tech }} /> Técnico bloqueado / calamidad</div>
          <div className="flex items-center gap-1.5"><AlertTriangle size={12} className="text-orange-600" /> Calamidad (posición del técnico)</div>
          <div className="font-bold text-[#0A3747] mt-1.5 mb-0.5">Órdenes (casa · techo = estado)</div>
          {Object.entries(BUCKET).map(([key, b]) => (
            <div key={key} className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm" style={{ background: b.color }} />{b.label}</div>
          ))}
          <div className="text-gray-500">Técnico seleccionado: azul = recorrido GPS · gris = OTs atendidas · punteado = próximas OTs en orden</div>
          {legendBranches.length > 0 && (
            <>
              <div className="font-bold text-[#0A3747] mt-1.5 mb-0.5">Sucursales</div>
              {legendBranches.map((b) => (
                <div key={b.id} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }} />{b.name}</div>
              ))}
            </>
          )}
        </div>

        {/* Panel de bloqueos y calamidades */}
        {panel === "blocks" && (
          <div className="absolute top-3 left-3 bottom-3 z-[600] w-[330px] bg-white rounded-xl border border-gray-200 shadow-xl flex flex-col" data-testid="warroom-blocks-panel">
            <div className="flex items-center gap-2 p-3 border-b">
              <ShieldAlert size={15} className="text-red-600" />
              <div className="font-black text-[#0A3747] text-sm flex-1">Bloqueos y calamidades</div>
              <button onClick={() => setPanel(null)} className="p-1 rounded hover:bg-gray-100"><X size={14} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3 text-xs">
              <Section title="Zonas, polígonos y sucursales" count={blocks.areas?.length}>
                {(blocks.areas || []).map((a) => (
                  <div key={a.id} className="p-2 rounded-lg border" style={{ borderColor: a.color, background: `${a.color}14` }}>
                    <div className="flex items-start gap-2">
                      <span className="w-3 h-3 rounded-sm mt-0.5" style={{ background: a.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{a.title}</div>
                        <div className="text-[10px] text-gray-600">{a.label} · {a.affected_ot_count} OTs · hasta {dmyhm(a.ends_at)}</div>
                        {!a.rings?.length && <div className="text-[10px] text-amber-700">Sin geometría para dibujar (la zona no tiene polígono).</div>}
                      </div>
                      <button onClick={() => resolveArea(a)} title="Levantar bloqueo" className="p-1 rounded hover:bg-white"><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </Section>
              <Section title="Técnicos bloqueados" count={blocks.techs?.length}>
                {(blocks.techs || []).map((b) => (
                  <div key={b.id} className="p-2 rounded-lg border" style={{ borderColor: reserved.tech, background: `${reserved.tech}14` }}>
                    <div className="flex items-start gap-2">
                      <span className="w-3 h-3 rounded-full mt-0.5 border-[3px]" style={{ borderColor: reserved.tech }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{b.tech_names.join(", ")}</div>
                        <div className="text-[10px] text-gray-600">{b.reason} · {dmyhm(b.from)} → {dmyhm(b.until)}{b.active_now ? " · vigente" : " · programado"}</div>
                      </div>
                      <button onClick={() => removeTechBlock(b)} title="Quitar bloqueo" className="p-1 rounded hover:bg-white"><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </Section>
              <Section title="Calamidades reportadas" count={blocks.calamities?.length}>
                {(blocks.calamities || []).map((c) => (
                  <button key={c.id} onClick={() => c.tech_id && setSel(c.tech_id)} className="w-full text-left p-2 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100">
                    <div className="flex items-center gap-1.5 font-bold"><AlertTriangle size={12} className="text-red-600" />{c.tech_name || "Técnico"} · {c.category}</div>
                    <div className="text-[10px] text-gray-600">{c.description || ""} · desde {dmyhm(c.since)}</div>
                  </button>
                ))}
              </Section>
            </div>
          </div>
        )}

        {loading && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] bg-white/95 border rounded-lg px-3 py-1.5 text-xs flex items-center gap-2 shadow">
            <Loader2 size={12} className="animate-spin" /> Cargando…
          </div>
        )}

        {/* Ficha del técnico */}
        {sel && (
          <TechWizard uid={sel} date={date} tech={selTech} onClose={() => setSel(null)}
            onFocus={() => setFitKey((x) => x + 1)} onOpenOt={(id) => navigate(`/ot-smart/${id}`)} />
        )}
      </div>

      {wizardOpen && (
        <BlockWizard onClose={() => setWizardOpen(false)} onDone={() => { setWizardOpen(false); load(true); }}
          branches={catalog.branches} zones={catalog.zones} technicians={technicians} reserved={reserved} />
      )}
      {colorsOpen && (
        <ColorsModal onClose={() => setColorsOpen(false)} onSaved={() => load(true)} reserved={reserved} />
      )}
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <div>
      <div className="font-bold text-[#0A3747] mb-1">{title} <span className="text-gray-400 font-normal">({count || 0})</span></div>
      <div className="space-y-1.5">{count ? children : <div className="text-[11px] text-gray-400">Ninguno vigente.</div>}</div>
    </div>
  );
}

// ───────────────────────── Ficha del técnico ─────────────────────────
function TechWizard({ uid, date, tech, onClose, onFocus, onOpenOt }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("resumen");
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/v1/warroom/cc/tech-detail/${uid}`, { params: { date } })
      .then(({ data }) => { if (alive) setD(data); })
      .catch((e) => { if (alive) { setD(null); toast.error(errMsg(e, "No se pudo cargar la ficha del técnico")); } })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [uid, date]);

  const stateLabel = tech?.state === "traveling" ? "Viajando" : tech?.state === "on_site" ? "En sitio" : "Disponible";
  return (
    <div className="absolute top-3 right-3 bottom-3 z-[700] w-[360px] bg-white rounded-xl border border-gray-200 shadow-2xl flex flex-col" data-testid="warroom-tech-wizard">
      <div className="p-3 border-b" style={{ borderTop: `5px solid ${tech?.branch_color || "#0A3747"}`, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
        <div className="flex items-start gap-2">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: tech?.branch_color || "#0A3747", boxShadow: `0 0 0 3px ${tech?.zone_color || "#fff"}` }}>
            {tech?.state === "traveling" ? <Truck size={16} /> : <UserIcon size={16} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-[#0A3747] text-sm truncate">{d?.name || tech?.name || "Técnico"}</div>
            <div className="text-[10px] text-gray-500 truncate">{d?.branch_name || tech?.branch_name || "Sin sucursal"}{tech?.zone_name ? ` · ${tech.zone_name}` : ""}</div>
            <div className="text-[10px] font-bold text-[#0891B2]">{stateLabel}{tech?.active_ot ? ` · ${tech.active_ot.number}` : ""}</div>
          </div>
          <button onClick={onFocus} title="Centrar en el mapa" className="p-1 rounded hover:bg-gray-100"><MapPin size={14} /></button>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="tech-wizard-close"><X size={14} /></button>
        </div>
        {tech?.state === "traveling" && tech.eta && (
          <div className="mt-2 p-2 rounded-lg bg-[#0A3747] text-white text-[11px]">
            Llega a <b>{tech.active_ot?.number}</b> en <b>{fmtMin(tech.eta.minutes)}</b> · {tech.eta.km} km · aprox. {hhmm(tech.eta.arrive_at)}
            <div className="text-[9px] opacity-75">Velocidad {tech.eta.speed_kmh} km/h ({tech.eta.speed_source})</div>
          </div>
        )}
        {(tech?.block || tech?.calamity) && (
          <div className="mt-2 p-2 rounded-lg text-[11px] border-2" style={{ borderColor: RESERVED_DEFAULT.tech }}>
            {tech.block && <div><b>Bloqueado:</b> {tech.block.reason} (hasta {dmyhm(tech.block.until)})</div>}
            {tech.calamity && <div className="text-red-600"><b>Calamidad:</b> {tech.calamity.category} {tech.calamity.description ? `· ${tech.calamity.description}` : ""}</div>}
          </div>
        )}
        <div className="flex gap-1 mt-2">
          {[["resumen", "Resumen"], ["ots", "Órdenes"]].map(([id, lb]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`flex-1 py-1 text-[11px] rounded-md font-bold ${tab === id ? "bg-[#0A3747] text-white" : "bg-gray-100 text-gray-600"}`}>{lb}</button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 text-xs">
        {loading && <div className="flex items-center gap-2 text-gray-500"><Loader2 size={12} className="animate-spin" /> Cargando…</div>}
        {!loading && d && tab === "resumen" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Stat icon={RouteIcon} label="KM recorridos" value={`${d.km_today ?? 0} km`} color="#7C3AED" />
              <Stat icon={ClipboardList} label="OTs del día" value={d.ots_total ?? 0} color="#0A3747" />
              <Stat icon={CircleDashed} label="Abiertas" value={d.ots_open ?? 0} color={BUCKET.pending.color} />
              <Stat icon={PlayCircle} label="En progreso" value={d.ots_in_progress ?? 0} color={BUCKET.in_progress.color} />
              <Stat icon={CheckCircle2} label="Cerradas" value={d.ots_done ?? 0} color={BUCKET.done.color} />
              <Stat icon={Clock} label="Conexión" value={fmtMin(tech?.kpi?.connection_min)} color="#2563EB" />
            </div>
            <div className="rounded-lg border p-2 space-y-1.5">
              <div className="font-bold text-[#0A3747] flex items-center gap-1"><Timer size={12} /> Tiempos promedio</div>
              <Row label="Atención en una OT (hoy)" value={fmtMin(d.avg_attention_min)} n={d.avg_attention_n} />
              <Row label="Recorrido entre OTs (hoy)" value={fmtMin(d.avg_travel_min)} n={d.avg_travel_n} />
              <Row label="Atención en una OT (30 días)" value={fmtMin(d.avg_attention_30d_min)} n={d.avg_attention_30d_n} />
              <Row label="Recorrido entre OTs (30 días)" value={fmtMin(d.avg_travel_30d_min)} n={d.avg_travel_30d_n} />
              <div className="text-[9px] text-gray-400">Atención: llegada al sitio → finalización. Recorrido: salida en ruta → llegada al sitio. Datos de la app móvil.</div>
            </div>
            <div className="text-[10px] text-gray-500">Línea azul en el mapa: recorrido GPS del día.</div>
          </div>
        )}
        {!loading && d && tab === "ots" && (
          <div className="space-y-3">
            {["in_progress", "pending", "done"].map((b) => {
              const list = (d.ots || []).filter((o) => o.bucket === b);
              const B = BUCKET[b];
              return (
                <div key={b}>
                  <div className="flex items-center gap-1.5 font-bold" style={{ color: B.color }}><B.Icon size={13} /> {B.label} ({list.length})</div>
                  {list.map((o) => (
                    <button key={o.odoo_task_id} onClick={() => onOpenOt(o.odoo_task_id)}
                      className="w-full text-left mt-1 p-2 rounded-lg border hover:bg-gray-50" style={{ borderColor: `${B.color}55` }}>
                      <div className="font-mono font-bold">{o.number} <span className="font-sans font-normal text-gray-500">· {hhmm(o.scheduled_at)}</span></div>
                      <div className="truncate">{o.client_name}</div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-lg border p-2">
      <div className="flex items-center gap-1 text-[9px] uppercase font-bold text-gray-500"><Icon size={11} style={{ color }} />{label}</div>
      <div className="text-lg font-black" style={{ color }}>{value}</div>
    </div>
  );
}
function Row({ label, value, n }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-600">{label}</span>
      <span className="font-bold text-[#0A3747]">{value}{n ? <span className="text-[9px] text-gray-400 font-normal"> · {n} OTs</span> : null}</span>
    </div>
  );
}

// ───────────────────────── Asistente de bloqueos ─────────────────────────
const toLocalInput = (d) => { const x = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return x.toISOString().slice(0, 16); };

function BlockWizard({ onClose, onDone, branches, zones, technicians, reserved }) {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [polyOpen, setPolyOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const now = new Date();
  const [form, setForm] = useState({
    title: "", category: "corte_via", description: "", zone_id: "", branch_id: "", tech_ids: [],
    starts: toLocalInput(now), ends: toLocalInput(new Date(now.getTime() + 4 * 3600 * 1000)), techSearch: "",
  });
  const upd = (k) => (e) => setForm((x) => ({ ...x, [k]: e.target.value }));

  const TYPES = [
    { id: "polygon", label: "Polígono", desc: "Dibuje el área en el mapa", Icon: Hexagon, color: reserved.polygon },
    { id: "zone", label: "Zona", desc: "Bloquea una zona completa", Icon: MapPin, color: reserved.polygon },
    { id: "branch", label: "Sucursal", desc: "Bloquea todas las zonas de la sucursal", Icon: Building2, color: reserved.branch },
    { id: "tech", label: "Técnico", desc: "Bloquea uno o varios técnicos", Icon: Users, color: reserved.tech },
  ];

  const choose = (t) => {
    setType(t);
    if (t === "polygon") { setPolyOpen(true); return; }
    setStep(2);
  };

  const valid = () => {
    if (!form.starts || !form.ends || new Date(form.ends) <= new Date(form.starts)) return "La fecha de fin debe ser mayor a la de inicio";
    if (type === "zone" && !form.zone_id) return "Seleccione la zona";
    if (type === "branch" && !form.branch_id) return "Seleccione la sucursal";
    if (type === "tech" && !form.tech_ids.length) return "Seleccione al menos un técnico";
    if (type !== "tech" && !form.title.trim()) return "Escriba un título";
    if (type === "tech" && !form.title.trim()) return "Escriba el motivo";
    return null;
  };

  const save = async () => {
    const v = valid();
    if (v) { toast.error(v); return; }
    setSaving(true);
    try {
      const starts = new Date(form.starts).toISOString();
      const ends = new Date(form.ends).toISOString();
      if (type === "tech") {
        await api.post("/v1/mass-events/tech-events", {
          tecnico_ids: form.tech_ids, motivo: form.title.trim(), desde: starts, hasta: ends, nota: form.description.trim(),
        });
        toast.success("Técnico(s) bloqueado(s)");
      } else {
        const body = {
          title: form.title.trim(), description: form.description.trim() || null, category: form.category,
          area_type: type, starts_at: starts, ends_at: ends,
          ...(type === "zone" ? { zone_id: form.zone_id } : {}), ...(type === "branch" ? { branch_id: form.branch_id } : {}),
        };
        const { data } = await api.post("/v1/mass-events", body);
        toast.success(`Bloqueo creado · ${data?.affected_ot_count ?? 0} OTs afectadas`);
      }
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo crear el bloqueo"));
    } finally { setSaving(false); }
  };

  if (polyOpen) {
    return <MassEventPolygonEditor open onClose={() => { setPolyOpen(false); onClose(); }} onCreated={() => { setPolyOpen(false); onDone(); }} />;
  }

  const tInfo = TYPES.find((t) => t.id === type);
  const techList = technicians.filter((t) => !form.techSearch || (t.name || t.email || "").toLowerCase().includes(form.techSearch.toLowerCase()));
  const zoneObj = zones.find((z) => z.id === form.zone_id);

  return (
    <Modal title={`Nuevo bloqueo · paso ${step} de 3${tInfo ? ` · ${tInfo.label}` : ""}`} onClose={onClose} testid="block-wizard">
      {step === 1 && (
        <div className="grid grid-cols-2 gap-2">
          {TYPES.map((t) => (
            <button key={t.id} onClick={() => choose(t.id)} data-testid={`block-type-${t.id}`}
              className="p-3 rounded-xl border-2 text-left hover:shadow-md transition" style={{ borderColor: t.color }}>
              <t.Icon size={20} style={{ color: t.id === "branch" ? "#A16207" : t.id === "tech" ? "#15803D" : t.color }} />
              <div className="font-black text-[#0A3747] text-sm mt-1">{t.label}</div>
              <div className="text-[11px] text-gray-500">{t.desc}</div>
            </button>
          ))}
        </div>
      )}
      {step === 2 && (
        <div className="space-y-2 text-xs">
          {type === "zone" && (
            <label className="block"><span className="font-bold text-[#0A3747]">Zona *</span>
              <select value={form.zone_id} onChange={upd("zone_id")} className="w-full mt-1 px-2 py-1.5 rounded border" data-testid="block-zone">
                <option value="">Seleccione…</option>
                {zones.map((z) => <option key={z.id} value={z.id}>{z.name}{z.polygon?.length ? "" : " (sin polígono)"}</option>)}
              </select>
              {zoneObj && !zoneObj.polygon?.length && <div className="text-[10px] text-amber-700 mt-0.5">Esta zona no tiene polígono: el bloqueo aplica a las OTs pero no se dibujará en el mapa.</div>}
            </label>
          )}
          {type === "branch" && (
            <label className="block"><span className="font-bold text-[#0A3747]">Sucursal *</span>
              <select value={form.branch_id} onChange={upd("branch_id")} className="w-full mt-1 px-2 py-1.5 rounded border" data-testid="block-branch">
                <option value="">Seleccione…</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          )}
          {type === "tech" && (
            <div>
              <span className="font-bold text-[#0A3747]">Técnicos / contratistas * ({form.tech_ids.length})</span>
              <input value={form.techSearch} onChange={upd("techSearch")} placeholder="Buscar…" className="w-full mt-1 px-2 py-1.5 rounded border" />
              <div className="max-h-44 overflow-y-auto border rounded mt-1 divide-y">
                {techList.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={form.tech_ids.includes(t.id)}
                      onChange={(e) => setForm((x) => ({ ...x, tech_ids: e.target.checked ? [...x.tech_ids, t.id] : x.tech_ids.filter((i) => i !== t.id) }))} />
                    {t.name || t.email}
                  </label>
                ))}
              </div>
            </div>
          )}
          <label className="block"><span className="font-bold text-[#0A3747]">{type === "tech" ? "Motivo *" : "Título *"}</span>
            <input value={form.title} onChange={upd("title")} className="w-full mt-1 px-2 py-1.5 rounded border" data-testid="block-title"
              placeholder={type === "tech" ? "Ej: Vacaciones, permiso médico, vehículo en taller" : "Ej: Corte de vía Av. Principal"} />
          </label>
          {type !== "tech" && (
            <label className="block"><span className="font-bold text-[#0A3747]">Categoría</span>
              <select value={form.category} onChange={upd("category")} className="w-full mt-1 px-2 py-1.5 rounded border">
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          )}
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="font-bold text-[#0A3747]">Desde *</span>
              <input type="datetime-local" value={form.starts} onChange={upd("starts")} className="w-full mt-1 px-2 py-1.5 rounded border" /></label>
            <label className="block"><span className="font-bold text-[#0A3747]">Hasta *</span>
              <input type="datetime-local" value={form.ends} onChange={upd("ends")} className="w-full mt-1 px-2 py-1.5 rounded border" /></label>
          </div>
          <label className="block"><span className="font-bold text-[#0A3747]">{type === "tech" ? "Nota" : "Descripción"}</span>
            <textarea value={form.description} onChange={upd("description")} rows={2} className="w-full mt-1 px-2 py-1.5 rounded border" /></label>
          <div className="flex gap-2 pt-2">
            <button onClick={() => setStep(1)} className="flex-1 py-2 rounded-lg border">Atrás</button>
            <button onClick={() => { const v = valid(); if (v) toast.error(v); else setStep(3); }} className="flex-1 py-2 rounded-lg bg-[#0A3747] text-white font-bold" data-testid="block-next">Revisar</button>
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-2 text-xs">
          <div className="p-3 rounded-xl border-2" style={{ borderColor: tInfo?.color }}>
            <div className="font-black text-[#0A3747]">{tInfo?.label}: {form.title}</div>
            {type === "zone" && <div>Zona: {zoneObj?.name}</div>}
            {type === "branch" && <div>Sucursal: {branches.find((b) => b.id === form.branch_id)?.name}</div>}
            {type === "tech" && <div>Técnicos: {technicians.filter((t) => form.tech_ids.includes(t.id)).map((t) => t.name || t.email).join(", ")}</div>}
            <div>Vigencia: {new Date(form.starts).toLocaleString("es-EC")} → {new Date(form.ends).toLocaleString("es-EC")}</div>
            <div className="text-[10px] text-gray-500 mt-1">
              {type === "tech" ? "Las OTs del técnico en ese periodo quedarán marcadas para reasignación." : "Las OTs dentro del área quedarán bloqueadas mientras esté vigente."}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => setStep(2)} className="flex-1 py-2 rounded-lg border">Atrás</button>
            <button onClick={save} disabled={saving} className="flex-1 py-2 rounded-lg bg-red-600 text-white font-bold disabled:opacity-50" data-testid="block-confirm">
              {saving ? "Guardando…" : "Confirmar bloqueo"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ───────────────────────── Colores por sucursal / zona ─────────────────────────
function ColorsModal({ onClose, onSaved, reserved }) {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(null);
  const loadColors = useCallback(() => api.get("/v1/warroom/cc/colors").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e, "No se pudieron cargar los colores"))), []);
  useEffect(() => { loadColors(); }, [loadColors]);

  const save = async (entity_type, entity_id, color) => {
    setSaving(`${entity_type}-${entity_id}`);
    try {
      await api.patch("/v1/warroom/cc/colors", { entity_type, entity_id, color });
      toast.success("Color guardado");
      await loadColors();
      onSaved();
    } catch (e) { toast.error(errMsg(e, "No se pudo guardar el color")); }
    finally { setSaving(null); }
  };

  return (
    <Modal title="Colores de sucursales y zonas" onClose={onClose} width={560} testid="colors-modal">
      <div className="text-[11px] p-2 rounded-lg bg-gray-50 border mb-3">
        <div className="font-bold text-[#0A3747] mb-1">Colores reservados para bloqueos (no se pueden usar):</div>
        <div className="flex flex-wrap gap-3">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: reserved.polygon }} /> Polígono / zona</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: reserved.branch }} /> Sucursal</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: reserved.tech }} /> Técnico</span>
        </div>
        <div className="text-gray-500 mt-1">El técnico se pinta con el color de su sucursal (relleno) y el de su zona (borde). Elija el color y pulse Guardar.</div>
      </div>
      {!d && <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 size={12} className="animate-spin" /> Cargando…</div>}
      {d && (
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <div className="font-bold text-[#0A3747] mb-1">Sucursales</div>
            {(d.branches || []).map((b) => <ColorRow key={`${b.id}-${b.color}`} type="branch" item={b} saving={saving === `branch-${b.id}`} onSave={save} />)}
          </div>
          <div>
            <div className="font-bold text-[#0A3747] mb-1">Zonas</div>
            {(d.zones || []).map((z) => <ColorRow key={`${z.id}-${z.color}`} type="zone" item={z} saving={saving === `zone-${z.id}`} onSave={save} />)}
          </div>
        </div>
      )}
    </Modal>
  );
}

function ColorRow({ type, item, saving, onSave }) {
  const [c, setC] = useState(item.color);
  const changed = String(c).toUpperCase() !== String(item.color).toUpperCase();
  return (
    <div className="flex items-center gap-2 py-1">
      <input type="color" value={c} onChange={(e) => setC(e.target.value)} className="w-8 h-7 rounded border cursor-pointer"
        data-testid={`color-${type}-${item.id}`} />
      <span className="flex-1 truncate">{item.name}</span>
      {!item.custom && !changed && <span className="text-[9px] text-gray-400">automático</span>}
      {changed && (
        <button onClick={() => onSave(type, item.id, c)} disabled={saving}
          className="px-2 py-0.5 rounded bg-[#0A3747] text-white text-[10px] font-bold disabled:opacity-50">
          {saving ? <Loader2 size={10} className="animate-spin" /> : "Guardar"}
        </button>
      )}
    </div>
  );
}
